<template>
  <li>
    <Modal
      :width="400"
      :value="defaultQuestionModalOpen"
      ctaText="Add"
      actions-border
      overlayColor="#ffffff"
      overlayOpacity="0.83"
      @cancel="defaultQuestionModalOpen = false"
      title="Add default question"
      @confirm="confirmDefaultQuestionModal">
      <template #default>
        <label class="default-question__label"> {{ $t("shortcodes.live_chat_auto.default_question") }} </label>
        <v-select
          v-model="defaultQuestionsValue"
          class="default-question__select"
          hide-details
          :items="defaultQuestionsOption"
          :menu-props="{
            offsetOverflow: true,
            offsetY: true,
          }"
          placeholder="Default question">
        </v-select>
      </template>
    </Modal>

    <span
      class="tree-item"
      :class="{
        'tree-item--human': node.human,
        'tree-item--bot': node.human === false,
        'tree-item--question': node.messageType === 'defaultQuestion',
        'tree-item--error': node._telHrefError,
      }">
      <div class="sender-indicator">
        <div class="d-flex align-center">
          <v-icon v-if="node.human" class="sender-indicator--human mr-2">mdi-account</v-icon>
          <v-icon v-else-if="node.messageType === 'defaultQuestion'" class="sender-indicator--forum mr-2">
            mdi-forum
          </v-icon>
          <v-icon v-else class="sender-indicator--bot mr-2">mdi-robot</v-icon>

          <span v-if="node.messageType === 'message'">
            {{ $t("shortcodes.live_chat_auto.message") }}
          </span>
          <span class="font-semibold" v-if="node.messageType === 'defaultQuestion'">
            {{ $t("shortcodes.live_chat_auto.default_question") }}
          </span>
          <span v-if="node.messageType === 'action'">
            {{ $t("shortcodes.live_chat_auto.action") }}
          </span>
          <span v-if="node.messageType === 'actionItem'">
            {{ $t("shortcodes.live_chat_auto.action_item") }}
            <!-- Just so it's clear what is the order of the action items -->
            <span v-if="node.messageType === 'actionItem'">{{ index + 1 }}</span>
          </span>
        </div>
      </div>
      <div v-if="parent" @click="removeNode" class="remove-node">
        <v-icon>mdi-trash-can-outline</v-icon>
      </div>
      <div v-if="node.messageType === 'actionItem'" @click="copyNode" class="copy-node">
        <v-icon>mdi-dock-window</v-icon>
      </div>
      <div>
        <div class="message-item">
          <LiveChatMessage ref="message" v-if="node.messageType === 'message'" :node.sync="node"></LiveChatMessage>
          <LiveChatDefaultQuestion
            ref="defaultQuestion"
            v-if="node.messageType === 'defaultQuestion'"
            :node.sync="node"></LiveChatDefaultQuestion>
          <LiveChatAction v-if="node.messageType === 'action'" :node.sync="node"></LiveChatAction>
          <LiveChatActionItem v-if="node.messageType === 'actionItem'" :node.sync="node"></LiveChatActionItem>
        </div>
        <v-btn class="mr-2" v-if="displayAddMessageBtn" @click="addMessage">{{
          $t("shortcodes.live_chat_auto.add_message")
        }}</v-btn>
        <v-btn v-if="displayAddActionBtn" @click="addAction">{{ $t("shortcodes.live_chat_auto.add_action") }}</v-btn>
        <v-btn v-if="displayAddMessageBtn" @click="openDefaultQuestionModal">{{
          $t("shortcodes.live_chat_auto.add_default_question")
        }}</v-btn>

        <v-btn v-if="displayAddActionItem" @click="addActionItem">{{
          $t("shortcodes.live_chat_auto.add_action_item")
        }}</v-btn>
      </div>
    </span>
    <ul v-if="node.children && node.children.length">
      <LiveChatAutoTreeNode
        v-for="(child, index) in node.children"
        ref="children"
        :index="index"
        :key="index"
        :node="child"
        :parent="node"
        @openModal="$emit('openModal', $event)"></LiveChatAutoTreeNode>
    </ul>
  </li>
</template>

<script>
  import cloneDeep from "lodash/cloneDeep";
  import LiveChatMessage from "../livechat/LiveChatMessage.vue";
  import LiveChatDefaultQuestion from "./LiveChatDefaultQuestion.vue";
  import LiveChatAction from "../livechat/LiveChatAction.vue";
  import LiveChatActionItem from "../livechat/LiveChatActionItem.vue";
  import { MESSAGE, ACTION, ACTION_ITEM, DEFAULT_QUESTION } from "../livechat/constant";
  import Modal from "@/components/custom/Modal.vue";
  import { LIVE_CHAT_DEFAULT_QUESTIONS } from "@/constant";

  /* eslint-disable */
  export default {
    name: "LiveChatAutoTreeNode",
    components: { LiveChatMessage, LiveChatAction, LiveChatActionItem, Modal, LiveChatDefaultQuestion },
    props: {
      node: {
        type: Object,
        required: true,
      },
      parent: {
        type: Object || null,
      },
      index: {
        type: Number,
      },
    },
    emits: ["openModal"],
    data() {
      return {
        defaultQuestionModalOpen: false,
        defaultQuestionsValue: "zip",
        defaultQuestionsOption: LIVE_CHAT_DEFAULT_QUESTIONS,
      };
    },
    computed: {
      displayAddMessageBtn() {
        // Action can't add message, only action items
        if (this.node.messageType === "action") return false;

        return true;
      },
      displayAddActionBtn() {
        // If there's already an action added, hide option to add an action
        if (this.node.children.some(childNode => childNode.messageType === "action")) return false;
        // TODO:
        // If current item is action item, hide option to add an action (maybe remove this)
        if (this.node.messageType === "actionItem") return false;
        // If current item is defaultQuestion, hide option to add an action (by design)
        if (this.node.messageType === "defaultQuestion") return false;
        // Action can't add action, only action items
        if (this.node.messageType === "action") return false;

        return true;
      },
      displayAddActionItem() {
        if (this.node.messageType !== "action") return false;

        return true;
      },
    },
    watch: {
      "node.content"() {
        if (this.node._telHrefError) this.$delete(this.node, "_telHrefError");
      },
      "node.text"() {
        if (this.node._telHrefError) this.$delete(this.node, "_telHrefError");
      },
      "node.value"() {
        if (this.node._telHrefError) this.$delete(this.node, "_telHrefError");
      },
    },
    methods: {
      copyNode() {
        const copiedNode = cloneDeep(this.node);
        let children = this.parent.children;
        this.parent.children = [];
        // as we clone a node, we need to access parent ref and update newly created node,
        // but with huge amount of refs its prone for mistakes and less efficient option is chosen
        this.$nextTick(() => {
          children.splice(this.index + 1, 0, copiedNode);
          this.parent.children = children;
        });
      },
      removeNode() {
        if (this.node.messageType === "actionItem" && this.node.children.length > 0) {
          this.$emit("openModal", {
            title: "Warning!",
            subtitle:
              "If an action item is deleted, all messages and other action items coming after it will also be deleted.",
            ctaText: "Delete",
            delete: true,
            index: this.index,
            parent: this.parent,
          });
        } else if (this.node.messageType === "action" && this.node.children.length > 0) {
          this.$emit("openModal", {
            title: "Cannot delete!",
            subtitle:
              "“Action” cannot be deleted, because it still has action items that belong to it. Action items will not function without “Action”.",
            ctaText: "Ok, understood",
            delete: false,
            cancelHidden: true,
          });
        } else if (this.node.children.length > 0) {
          const children = this.node.children;

          this.updateTreeContent(children, this);
          this.parent.children = children;
        } else {
          this.parent.children.splice(this.parent.children.indexOf(this.node), 1);
        }
      },
      openDefaultQuestionModal() {
        this.defaultQuestionModalOpen = true;
      },
      confirmDefaultQuestionModal() {
        this.defaultQuestionModalOpen = false;
        this.addDefaultQuestion();
      },
      addAction() {
        const action = cloneDeep(ACTION);
        if (this.node.children.length > 0) {
          const actionItem = cloneDeep(ACTION_ITEM);
          const children = this.node.children;
          action.children = [actionItem];
          actionItem.children = children;
          this.node.children = [action];
        } else {
          this.node.children.push(action);
        }
      },
      addActionItem() {
        const actionItem = cloneDeep(ACTION_ITEM);
        this.node.children.push(actionItem);
      },
      addMessage() {
        const message = cloneDeep(MESSAGE);
        if (this.node.children.length > 0) {
          const children = this.node.children;
          message.children = children;
          this.updateTreeContent([message], this.$refs.children[0]);
          this.node.children = [message];
        } else {
          this.node.children.push(message);
        }
      },
      addDefaultQuestion() {
        const defaultQuestion = cloneDeep(DEFAULT_QUESTION);
        defaultQuestion.defaultQuestionType = this.defaultQuestionsValue;
        defaultQuestion.content = `<strong>${
          this.defaultQuestionsOption.find(option => option.value === this.defaultQuestionsValue).text
        }</strong>`;
        if (this.node.children.length > 0) {
          const children = this.node.children;
          defaultQuestion.children = children;
          this.updateTreeContent([defaultQuestion], this.$refs.children[0]);
          this.node.children = [defaultQuestion];
        } else {
          this.node.children.push(defaultQuestion);
        }
      },
      // Edit at your own risk
      updateTreeContent(children, ref) {
        let index = ref?.index ? ref.index : 0;
        if (children[index].messageType === "defaultQuestion" && ref && ref.$refs.defaultQuestion) {
          ref.$refs.defaultQuestion.updateContent(children[index].content);
          if (children[index].children?.length > 0 && ref.$refs.children) {
            this.updateTreeContent(children[index].children, ref.$refs.children[index]);
          }
        } else if (children[index].messageType === "message" && ref && ref.$refs.message) {
          ref.$refs.message.updateContent(children[index].content);
          if (children[index].children?.length > 0 && ref.$refs.children) {
            this.updateTreeContent(children[index].children, ref.$refs.children[index]);
          }
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/typography/index.scss";
  @import "@core/preset/preset/functions.scss";
  .sender-indicator {
    position: absolute;
    top: 10px;
    left: 10px;
    &--human {
      color: green !important;
    }

    &--bot {
      color: orange !important;
    }

    &--forum {
      color: #2377f8 !important;
    }
  }

  .tree-item {
    padding: 45px 10px 10px 10px;
    border-radius: 5px;
    &--human {
      border: 1px solid green !important;
    }
    &--bot {
      border: 1px solid orange !important;
    }

    &--question {
      border: 2px solid #2377f8 !important;
    }
    &--error {
      border: 3px dashed get-color("error") !important;
    }
  }

  .remove-node {
    padding: 5px;
    position: absolute;
    top: 0;
    right: 0;
    cursor: pointer;
    z-index: 2;
  }

  .copy-node {
    padding: 5px 35px 5px 5px;
    position: absolute;
    top: 0;
    right: 0;
    cursor: pointer;
    z-index: 1;
  }

  .default-question {
    &__label {
      color: get-color("subtitle");
      @include text-caption-bold();
    }

    &__select {
      padding-top: 0;
    }

    &__border {
      border-top: 1px solid get-color("border");
    }
  }

  .font-semibold {
    font-weight: 600;
  }
</style>
